import { Box, Flex, Text, HStack, VStack, useColorModeValue, Progress, Badge, useBreakpointValue } from '@chakra-ui/react';
import { FiInfo, FiMoreVertical, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from './IconWrapper';
import CachedImage from './CachedImage';
import ContinueWatchingCard from './ContinueWatchingCard';
import SubscribedCreatorCard from './SubscribedCreatorCard';
import { ContentItem as ServiceContentItem } from '../services/contentService';

interface ContentItem {
  id: string;
  title: string;
  type: string;
  thumbnail: string;
  rank?: number;
  progress?: number;
  episode?: string;
  duration?: string;
  creator?: string;
  newEpisodes?: number;
  season?: string;
  episodeNumber?: number;
  // For subscribed creators
  name?: string;
  avatar?: string;
  followers?: number;
  newContent?: number;
  isOnline?: boolean;
  lastActive?: string;
  verified?: boolean;
}

interface ContentRowProps {
  title: string;
  items: (ServiceContentItem | ContentItem)[];
  onSeeAll?: () => void;
  isContinueWatching?: boolean;
  isSubscribed?: boolean;
}

const ContentRow: React.FC<ContentRowProps> = ({ title, items, onSeeAll, isContinueWatching, isSubscribed }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const titleColor = useColorModeValue('gray.900', 'white');
  const itemTitleColor = useColorModeValue('gray.800', 'white');
  const seeAllColor = useColorModeValue('purple.600', 'purple.400');
  const seeAllHoverColor = useColorModeValue('purple.700', 'purple.300');

  const handleItemClick = (item: ServiceContentItem | ContentItem) => {
    const category = 'category' in item ? item.category : item.type;
    if (category === 'book') {
      navigate(`/book/${item.id}`);
    } else if (category === 'short') {
      navigate(`/short/${item.id}`);
    } else if (category === 'comic') {
      navigate(`/comic/${item.id}`);
    } else {
      navigate(`/content/${item.id}`);
    }
  };

  const mapToContinueWatchingItem = (item: ServiceContentItem | ContentItem) => {
    if ('category' in item) {
      return {
        id: item.id,
        title: item.title,
        type: item.category,
        thumbnail: item.thumbnail,
        progress: 0,
        episode: '',
        duration: item.duration ? `${item.duration} min` : '',
        episodeNumber: 0
      };
    } else {
      return {
        id: item.id,
        title: item.title,
        type: item.type,
        thumbnail: item.thumbnail,
        progress: item.progress || 0,
        episode: item.episode || '',
        duration: item.duration || '',
        episodeNumber: item.episodeNumber || 0
      };
    }
  };

  const mapToCreatorData = (item: ServiceContentItem | ContentItem) => {
    if ('category' in item) {
      return {
        id: item.id,
        name: item.uploader,
        avatar: item.uploaderAvatar,
        followers: 0,
        newContent: 0,
        isOnline: false,
        lastActive: '',
        verified: false
      };
    } else {
      return {
        id: item.id,
        name: item.name || item.title,
        avatar: item.avatar,
        followers: item.followers || 0,
        newContent: item.newContent || 0,
        isOnline: item.isOnline || false,
        lastActive: item.lastActive || '',
        verified: item.verified || false
      };
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontWeight="bold" color={titleColor}>
          {title}
        </Text>
        <Text
          fontSize="sm"
          color={seeAllColor}
          cursor="pointer"
          _hover={{ color: seeAllHoverColor }}
          onClick={onSeeAll}
        >
          See all
        </Text>
      </Flex>
      
      <Box 
        overflowX="auto" 
        pb={2}
        css={{
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#A0AEC0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#718096',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#A0AEC0 transparent',
        }}
      >
        <HStack gap={{ base: 2, sm: 3, md: 4 }} align="start" minW="max-content">
          {items.map((item) => {
            // Use specialized components for Continue Watching and Subscribed Creators
            if (isContinueWatching) {
              return (
                <ContinueWatchingCard
                  key={item.id}
                  item={mapToContinueWatchingItem(item)}
                  onPlay={() => handleItemClick(item)}
                  onInfo={() => handleItemClick(item)}
                />
              );
            }

            if (isSubscribed) {
              return (
                <SubscribedCreatorCard
                  key={item.id}
                  creator={mapToCreatorData(item)}
                  onUnsubscribe={() => {
                    // Handle unsubscribe
                  }}
                  onNotificationToggle={() => {
                    // Handle notification toggle
                  }}
                />
              );
            }

            // Default content card
            return (
              <VStack
                key={item.id}
                gap={2}
                minW={{ base: "140px", sm: "180px", md: "200px", lg: "250px" }}
                cursor="pointer"
                onClick={() => handleItemClick(item)}
                _hover={{ transform: 'scale(1.05)' }}
                transition="transform 0.2s"
              >
              <Box 
                position="relative" 
                borderRadius="md" 
                overflow="hidden"
                w={{ base: "140px", sm: "180px", md: "200px", lg: "250px" }}
                h={{ base: "95px", sm: "120px", md: "130px", lg: "150px" }}
                bg="gray.100"
                _dark={{ bg: "gray.700" }}
                boxShadow="md"
                _hover={{ 
                  boxShadow: "lg",
                  transform: "scale(1.02)",
                  transition: "all 0.2s ease-in-out"
                }}
              >
                <Box
                  position="relative"
                  w="100%"
                  h="100%"
                  overflow="hidden"
                  borderRadius="md"
                >
                  <CachedImage
                    src={item.thumbnail}
                    alt={item.title}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    borderRadius="md"
                    fallbackType={'category' in item ? item.category : item.type}
                    size="w342"
                  />
                  
                  {/* Overlay gradient for better text readability */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 100%)"
                    borderRadius="md"
                  />
                  
                  {/* Action buttons container */}
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    display="flex"
                    gap={1}
                  >
                    <Box
                      bg="blackAlpha.600"
                      borderRadius="full"
                      p={1}
                      backdropFilter="blur(4px)"
                    >
                      <IconWrapper icon={FiMoreVertical} size={16} />
                    </Box>
                  </Box>
                  
                  {/* Rank number for Top 50 lists - not applicable for service content items */}
                  
                  <Box
                    position="absolute"
                    bottom={2}
                    left={2}
                    bg="blackAlpha.600"
                    borderRadius="full"
                    p={1}
                    backdropFilter="blur(4px)"
                    cursor="pointer"
                    onClick={() => handleItemClick(item)}
                    _hover={{ bg: "blackAlpha.700" }}
                  >
                    <IconWrapper icon={FiInfo} size={16} />
                  </Box>
                </Box>
              </Box>
              
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={itemTitleColor}
                textAlign="center"
                noOfLines={2}
              >
                {item.title}
              </Text>
              <Badge colorScheme="purple" variant="subtle">
                {'category' in item ? item.category : item.type}
              </Badge>
              
              {/* Continue Watching Progress - not applicable for service content items */}
              
              {/* Subscribed Content Info - not applicable for service content items */}
            </VStack>
            );
          })}
        </HStack>
      </Box>
    </VStack>
  );
};

export default ContentRow;
